import * as React from "react";
import {
    Panel,
    PanelType,
    Stack,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    Icon,
    IColumn,
    DetailsList,
    SelectionMode,
    FontSizes,
    FontWeights,
    mergeStyles,
    Link,
    IconButton,
} from "@fluentui/react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { getSubscriptionPayments, resolveSubscriptionPaymentMethods, SubscriptionPaymentItem } from "../../utilities/helpers/ApiHelper";

interface SubscriptionPaymentsPanelProps {
    isOpen: boolean;
    subscriptionId: number;
    onDismiss: () => void;
}

// Status → badge palette. Values come from InvoiceHandler.MapInvoiceStatus which writes
// "paid" / "failed" / "pending" / "finalization_failed" / "unknown" — the long Stripe event
// names ("payment_failed", "payment_action_required") are kept as aliases so a future renaming
// of the projection layer doesn't silently downgrade error rows to neutral grey. Anything not
// listed (legacy Mollie statuses like "open", "expired", "canceled") falls through to grey.
const statusBadgeStyle = (status: string | null | undefined): React.CSSProperties => {
    const base: React.CSSProperties = {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: FontSizes.size12,
        fontWeight: FontWeights.semibold,
    };
    switch ((status ?? "").toLowerCase()) {
        case "paid":
            return { ...base, backgroundColor: "#dff6dd", color: "#107c10" };
        case "pending":
        case "payment_action_required":
            return { ...base, backgroundColor: "#fff4ce", color: "#797673" };
        case "failed":
        case "payment_failed":
        case "finalization_failed":
            return { ...base, backgroundColor: "#fde7e9", color: "#a4262c" };
        default:
            return { ...base, backgroundColor: "#f3f2f1", color: "#605e5c" };
    }
};

const formatTimestamp = (iso: string): string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
};

const linkActionsStyle = mergeStyles({
    display: "flex",
    alignItems: "center",
    gap: 6,
});

// Server-side sentinel (StripeBillingService.NoInstrumentSentinel) written to Payment.Method for a
// paid invoice that settled with no chargeable instrument (zero-amount / credit / out-of-band). It
// means "resolved, nothing to show": render blank and never re-POST for it.
const NO_INSTRUMENT_METHOD = "-";

// A paid Stripe row whose instrument isn't resolved yet: Method is null/"" or the pre-de-conflation
// "stripe" placeholder. Real instruments AND the NO_INSTRUMENT_METHOD sentinel are non-empty and
// != "stripe", so they fall OUTSIDE this set (treated as resolved → no re-POST on later opens).
const isUnresolvedPaid = (r: SubscriptionPaymentItem): boolean =>
    (r.status ?? "").toLowerCase() === "paid" && !!r.transactionId && (!r.method || r.method === "stripe");

const SubscriptionPaymentsPanel: React.FC<SubscriptionPaymentsPanelProps> = ({
    isOpen,
    subscriptionId,
    onDismiss,
}) => {
    const languageStrings = React.useContext(LanguageContext);
    const [items, setItems] = React.useState<SubscriptionPaymentItem[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [isResolvingMethods, setIsResolvingMethods] = React.useState(false);

    // The Phase-2 resolve is async; guard setState against the panel closing before it returns.
    const mountedRef = React.useRef(true);
    React.useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const load = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        setIsResolvingMethods(false);
        const res = await getSubscriptionPayments(subscriptionId);
        if (!mountedRef.current) return;
        if (res === null) {
            setError(languageStrings.SubscriptionPaymentsLoadFailed ?? "Failed to load payments.");
            setItems([]);
            setIsLoading(false);
            return;
        }
        setItems(res);
        setIsLoading(false);

        // Phase 2: resolve + persist the payment instrument for any paid row still missing one,
        // then refresh. Best-effort — on failure the "Payment method" column simply stays blank.
        if (res.some(isUnresolvedPaid)) {
            setIsResolvingMethods(true);
            const enriched = await resolveSubscriptionPaymentMethods(subscriptionId);
            if (!mountedRef.current) return;
            if (enriched && enriched.length > 0) {
                setItems(enriched);
            }
            setIsResolvingMethods(false);
        }
    }, [subscriptionId, languageStrings]);

    React.useEffect(() => {
        if (isOpen) {
            load();
        }
    }, [isOpen, load]);

    const columns: IColumn[] = React.useMemo(() => [
        {
            key: "createdDate",
            name: languageStrings.Date ?? "Date",
            fieldName: "createdDate",
            minWidth: 130,
            maxWidth: 160,
            isResizable: true,
            onRender: (item: SubscriptionPaymentItem) => formatTimestamp(item.createdDate),
        },
        {
            key: "status",
            name: languageStrings.Status,
            fieldName: "status",
            minWidth: 90,
            maxWidth: 140,
            isResizable: true,
            onRender: (item: SubscriptionPaymentItem) => (
                <span style={statusBadgeStyle(item.status)}>{item.status}</span>
            ),
        },
        {
            key: "description",
            name: languageStrings.Description,
            fieldName: "description",
            minWidth: 200,
            maxWidth: 420,
            isResizable: true,
            isMultiline: true,
            onRender: (item: SubscriptionPaymentItem) => (
                <span style={{ whiteSpace: "normal" }}>{item.description ?? ""}</span>
            ),
        },
        {
            key: "amount",
            name: languageStrings.Amount,
            fieldName: "amountString",
            minWidth: 90,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: SubscriptionPaymentItem) =>
                item.amountString || item.amountValue?.toString() || "",
        },
        {
            key: "paymentMethod",
            name: "Payment method",
            fieldName: "method",
            minWidth: 130,
            maxWidth: 180,
            isResizable: true,
            onRender: (item: SubscriptionPaymentItem) => {
                const resolved = item.method && item.method !== "stripe" && item.method !== NO_INSTRUMENT_METHOD
                    ? item.method : null;
                if (resolved) return resolved;
                // Pending Phase-2 resolution: subtle placeholder while the second call is in flight.
                if (isResolvingMethods && isUnresolvedPaid(item)) {
                    return <span style={{ color: "#a19f9d" }}>…</span>;
                }
                return "";
            },
        },
        {
            key: "invoice",
            name: languageStrings.Invoice,
            minWidth: 140,
            maxWidth: 180,
            isResizable: true,
            onRender: (item: SubscriptionPaymentItem) => {
                const hasHosted = !!item.hostedInvoiceUrl;
                const hasPdf = !!item.invoicePdfUrl;
                if (!hasHosted && !hasPdf) {
                    return null;
                }
                return (
                    <div className={linkActionsStyle}>
                        {hasHosted && (
                            <Link
                                href={item.hostedInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                styles={{ root: { fontSize: FontSizes.size12 } }}
                            >
                                {languageStrings.ViewInvoice ?? "View invoice"}
                            </Link>
                        )}
                        {hasPdf && (
                            <IconButton
                                iconProps={{ iconName: "PDF" }}
                                title={languageStrings.DownloadInvoicePdf ?? "Download PDF"}
                                ariaLabel={languageStrings.DownloadInvoicePdf ?? "Download PDF"}
                                href={item.invoicePdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                styles={{ root: { height: 24, width: 24 }, icon: { fontSize: 14 } }}
                            />
                        )}
                    </div>
                );
            },
        },
    ], [languageStrings, isResolvingMethods]);

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            headerText={languageStrings.SubscriptionPayments ?? "Subscription payments"}
            type={PanelType.large}
            closeButtonAriaLabel={languageStrings.Close ?? "Close"}
        >
            <Stack tokens={{ childrenGap: 12 }} style={{ paddingTop: 8 }}>
                {isLoading && (
                    <Spinner size={SpinnerSize.medium} label={languageStrings.Loading ?? "Loading..."} />
                )}
                {!isLoading && error && (
                    <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
                )}
                {!isLoading && !error && items.length === 0 && (
                    <div style={{ textAlign: "center", padding: 24, color: "#605e5c" }}>
                        <Icon iconName="Money" style={{ fontSize: 24, marginBottom: 8 }} />
                        <div>{languageStrings.NoSubscriptionPayments ?? "No payments yet."}</div>
                    </div>
                )}
                {!isLoading && !error && items.length > 0 && (
                    <DetailsList
                        setKey="payments"
                        items={items}
                        columns={columns}
                        selectionMode={SelectionMode.none}
                        getKey={(item: SubscriptionPaymentItem) => item.id.toString()}
                    />
                )}
            </Stack>
        </Panel>
    );
};

export default SubscriptionPaymentsPanel;
